import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function CoordinatorMapModal({ open, onClose, coordinator, academicYear, statusByTeamFilter, clientBillingStatusFilter, selectedService, orderSource, props }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapsError, setMapsError] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const googleMapsScriptRef = useRef(null);

  useEffect(() => {
    if (open && academicYear) {
      loadCompanies();
    } else {
      // Clean up markers when modal closes
      if (markersRef.current.length > 0) {
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
    }
  }, [open, academicYear]);

  useEffect(() => {
    if (!open) {
      setMapsError(null);
      return;
    }

    if (!MAPS_KEY) {
      setMapsError(
        'Google Maps API key is not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY in frontendedubriczsignup/.env.local, enable the Maps JavaScript API (and billing) on that key in Google Cloud Console, and allow this origin under HTTP referrer restrictions (e.g. http://localhost:3000/*). Restart npm start after changing .env.'
      );
      return;
    }

    if (googleMapsScriptRef.current) {
      return;
    }

    const prevGmAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      setMapsError(
        'Google Maps rejected this API key (gm_authFailure). Check that Maps JavaScript API is enabled, billing is active, and Application restrictions include this site URL.'
      );
      if (typeof prevGmAuthFailure === 'function') {
        prevGmAuthFailure();
      }
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(MAPS_KEY)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (open && companies.length > 0) {
        initializeMap();
      }
    };
    script.onerror = () => {
      setMapsError('Failed to load the Google Maps script. Check your network and Content Security Policy.');
      console.error('Failed to load Google Maps script');
    };
    document.head.appendChild(script);
    googleMapsScriptRef.current = script;

    return () => {
      window.gm_authFailure = prevGmAuthFailure;
      if (googleMapsScriptRef.current && document.head.contains(googleMapsScriptRef.current)) {
        document.head.removeChild(googleMapsScriptRef.current);
      }
      googleMapsScriptRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (open && companies.length > 0 && window.google && window.google.maps) {
      initializeMap();
    }
  }, [companies, open]);

  const loadCompanies = () => {
    if (!academicYear) return;
    setLoading(true);
    const params = {
      year: academicYear,
      ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
      ...(orderSource && { order_source: orderSource }),
    };
    if (clientBillingStatusFilter) {
      params.client_billing_status = clientBillingStatusFilter;
    }
    if (selectedService?.id) {
      params.service = selectedService.id;
    }

    getRequest(`${GET_URL.billing_config.api}companies-map-locations/`, params, props)
      .then((response) => {
        setLoading(false);
        if (response && response.status === 200) {
          const data = response.data?.data || [];
          setCompanies(data);
        }
      })
      .catch(() => {
        setLoading(false);
        setCompanies([]);
      });
  };

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) return;
    if (mapInstanceRef.current) {
      // Map already initialized, just update markers
      updateMarkers();
      return;
    }

    // Initialize map centered on India
    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 5,
      center: { lat: 20.5937, lng: 78.9629 }, // Center of India
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
    });

    mapInstanceRef.current = map;
    updateMarkers();
  };

  const updateMarkers = () => {
    if (!mapInstanceRef.current || !window.google || !window.google.maps) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    if (companies.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidLocation = false;

    companies.forEach((company) => {
      const lat = parseFloat(company.latitude);
      const lng = parseFloat(company.longitude);

      if (isNaN(lat) || isNaN(lng) || lat < 11.0 || lat > 19.0 || lng < 73.5 || lng > 79.0) return;

      const position = { lat, lng };
      bounds.extend(position);
      hasValidLocation = true;

      // Determine if this company belongs to the selected coordinator
      const isSelectedCoordinator = coordinator?.coordinator_id && 
        company.coordinator_id === coordinator.coordinator_id;

      // Create marker with different color for selected coordinator's companies
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        title: company.company_name,
        icon: isSelectedCoordinator
          ? {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#FF0000', // Red for selected coordinator's companies
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            }
          : {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#4285F4', // Blue for other companies
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            },
      });

      // Create info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <strong>${company.company_name || 'N/A'}</strong><br/>
            ${company.billing_address ? `<div style="margin-top: 4px;">${company.billing_address}</div>` : ''}
            ${company.primary_contact_name ? `<div style="margin-top: 4px;">Contact: ${company.primary_contact_name}</div>` : ''}
            ${company.primary_contact_phone_number ? `<div>Phone: ${company.primary_contact_phone_number}</div>` : ''}
            ${company.coordinator_name ? `<div style="margin-top: 4px; color: ${isSelectedCoordinator ? '#FF0000' : '#666'}; font-weight: ${isSelectedCoordinator ? 'bold' : 'normal'};">Coordinator: ${company.coordinator_name}</div>` : ''}
          </div>
        `,
      });

      marker.addListener('click', () => {
        // Close all other info windows
        markersRef.current.forEach(m => {
          if (m.infoWindow) {
            m.infoWindow.close();
          }
        });
        infoWindow.open(mapInstanceRef.current, marker);
      });

      marker.infoWindow = infoWindow;
      markersRef.current.push(marker);
    });

    // Fit bounds to show all markers
    if (hasValidLocation) {
      mapInstanceRef.current.fitBounds(bounds);
      // Ensure minimum zoom level
      const listener = window.google.maps.event.addListener(mapInstanceRef.current, 'bounds_changed', () => {
        if (mapInstanceRef.current.getZoom() > 15) {
          mapInstanceRef.current.setZoom(15);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Company Locations Map
            {coordinator?.coordinator_name && (
              <Typography component="span" variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                (Highlighted: {coordinator.coordinator_name})
              </Typography>
            )}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
            <CircularProgress />
          </Box>
        ) : mapsError ? (
          <Box py={4} px={2}>
            <Typography variant="body2" color="error">
              {mapsError}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                <span style={{ color: '#FF0000', fontWeight: 'bold' }}>●</span> Red markers: Companies assigned to selected coordinator
                <span style={{ marginLeft: '16px', color: '#4285F4', fontWeight: 'bold' }}>●</span> Blue markers: Other companies
              </Typography>
            </Box>
            <Box
              ref={mapRef}
              sx={{
                width: '100%',
                height: '500px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
            {companies.length === 0 && !loading && (
              <Box mt={2} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  No companies with map locations found for the selected filters.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CoordinatorMapModal;

